import type { AiBranch, AiBranchView, AiEvent } from '@swiftpay/shared';
import { apiFetch } from './client.js';

/** ИИ аналитик: запросы к /api/ai/* (бэкенд — прокладка к агенту на ноутбуке). */
export type AiAskResult = { turnId: string; branchId: number };
export type AiAnswer = { index: number } | { text: string } | Record<string, never>;

export const aiApi = {
  access: () => apiFetch<{ enabled: boolean }>('/api/ai/access'),
  ask: (question: string, branchId?: number) =>
    apiFetch<AiAskResult>('/api/ai/ask', {
      method: 'POST',
      body: JSON.stringify(branchId ? { question, branchId } : { question }),
    }),
  /** Голосовой вопрос: запись уходит сырым телом, Content-Type — тип записи (apiFetch не подменяет его на JSON). */
  askVoice: (blob: Blob, mimeType: string, branchId?: number) =>
    apiFetch<AiAskResult>(`/api/ai/ask-voice${branchId ? `?branchId=${branchId}` : ''}`, {
      method: 'POST',
      headers: { 'Content-Type': mimeType },
      body: blob,
    }),
  choice: (turnId: string, answer: AiAnswer) =>
    apiFetch<{ ok: true }>(`/api/ai/turns/${turnId}/choice`, { method: 'POST', body: JSON.stringify(answer) }),
  cancel: (turnId: string) => apiFetch<{ ok: true }>(`/api/ai/turns/${turnId}/cancel`, { method: 'POST', body: '{}' }),
  branches: () => apiFetch<AiBranch[]>('/api/ai/branches'),
  branch: (id: number) => apiFetch<AiBranchView>(`/api/ai/branches/${id}`),
};

/** Разбор потока SSE: события из накопленного текста и незаконченный хвост.
 *  Строки-комментарии (`: keepalive`) пропускаются. */
export function parseSse(buffer: string): { events: AiEvent[]; rest: string } {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  const events: AiEvent[] = [];
  for (const part of parts) {
    for (const line of part.split('\n')) {
      if (line.startsWith('data:')) events.push(JSON.parse(line.slice(5)) as AiEvent);
    }
  }
  return { events, rest };
}

/** Поток событий хода через fetch (EventSource сам переподключается и получил бы события повторно).
 *  Завершается на событии done или при отмене signal (уход со страницы — агент доводит ход сам). */
export async function streamTurn(turnId: string, onEvent: (e: AiEvent) => void, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`/api/ai/turns/${turnId}/events`, { credentials: 'include', ...(signal ? { signal } : {}) });
  if (!res.ok || !res.body) throw Object.assign(new Error(`http_${res.status}`), { status: res.status });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    const parsed = parseSse(buffer + decoder.decode(value, { stream: true }));
    buffer = parsed.rest;
    for (const event of parsed.events) {
      onEvent(event);
      if (event.kind === 'done') return;
    }
  }
}
